/** zod schemas for forms and API payloads. Messages are i18n keys, translated in the UI. */
import { z } from 'zod';
import { isIsoDate } from '../calc/dates.js';
import { PERM_KEYS, ROLES } from '../perms.js';

const trimmed = z.string().trim();
const optionalText = trimmed
  .max(2000)
  .optional()
  .nullable()
  .transform((v) => (v ? v : null));
export const isoDate = z.string().refine(isIsoDate, { message: 'errors.invalid_date' });
const optionalDate = isoDate
  .optional()
  .nullable()
  .or(z.literal('').transform(() => null));

/** Israeli mobile or international phone → E.164 without '+', e.g. 972501234567 */
export function normalizePhone(raw: string): string | null {
  const digits = raw.replace(/[^\d+]/g, '');
  if (/^05\d{8}$/.test(digits)) return `972${digits.slice(1)}`;
  if (/^\+?9725\d{8}$/.test(digits)) return digits.replace('+', '');
  if (/^\+\d{8,15}$/.test(digits)) return digits.slice(1);
  return null;
}

export const phoneSchema = trimmed.transform((v, ctx) => {
  const p = normalizePhone(v);
  if (!p) {
    ctx.addIssue({ code: 'custom', message: 'errors.invalid_phone' });
    return z.NEVER;
  }
  return p;
});

export const partySchema = z.object({
  name: trimmed.min(1, 'errors.required').max(200),
  contact: optionalText,
  phone: optionalText,
  email: z
    .email('errors.invalid_email')
    .optional()
    .nullable()
    .or(z.literal('').transform(() => null)),
  address: optionalText,
  pay_days: z.coerce.number().int().min(0).max(365).optional().nullable(),
  note: optionalText,
});
export type PartyInput = z.input<typeof partySchema>;

export const supplierSchema = partySchema.extend({ crops: optionalText });

export const passwordSchema = z.string().min(8, 'errors.password_short').max(72);
export const setPasswordSchema = z.object({ password: passwordSchema });

export const inviteUserSchema = z
  .object({
    full_name: trimmed.min(1, 'errors.required').max(120),
    email: z
      .email('errors.invalid_email')
      .optional()
      .nullable()
      .or(z.literal('').transform(() => null)),
    phone: phoneSchema
      .optional()
      .nullable()
      .or(z.literal('').transform(() => null)),
    role: z.enum(ROLES),
    perms: z.partialRecord(z.enum(PERM_KEYS), z.boolean()).default({}),
    locale: z.enum(['he', 'th', 'en']).default('he'),
    password: passwordSchema.optional().or(z.literal('').transform(() => undefined)),
    /** the employee this login belongs to (FarmAI), linked on creation */
    employee_id: z.uuid().optional(),
  })
  // A worker may have neither and sign in with a QR code only.
  .refine((u) => u.email || u.phone || u.role === 'worker', {
    message: 'errors.email_or_phone',
    path: ['email'],
  });
export type InviteUserInput = z.input<typeof inviteUserSchema>;

export const employeeSchema = z.object({
  first_name: trimmed.min(1, 'errors.required').max(100),
  last_name: optionalText,
  nick: optionalText,
  name_thai: optionalText,
  emp_no: optionalText,
  clock_no: optionalText,
  is_lead: z.boolean().default(false),
  lead_id: z.uuid().optional().nullable(),
  team: optionalText,
  specialties: z.array(trimmed).default([]),
  start_date: optionalDate,
  end_date: optionalDate,
  phone: optionalText,
  email: optionalText,
  note: optionalText,
  notice_given: z.boolean().default(false),
});

export const employeePrivateSchema = z.object({
  birth_date: optionalDate,
  nationality: optionalText,
  passport_no: optionalText,
  passport_expiry: optionalDate,
  visa_expiry: optionalDate,
});

const crop = z.string().regex(/^[A-Z]$/);
const money = z.coerce.number().min(0).max(1_000_000);
const quantity = z.coerce.number().positive('errors.positive').max(1_000_000);

export const harvestDaySchema = z.object({
  date: isoDate,
  note: optionalText,
  entries: z.array(
    z
      .object({
        crop: crop.refine((c) => c !== 'X'),
        greenhouse_id: z.uuid(),
        kg: z.coerce.number().min(0).max(10_000),
        ng: z.coerce.number().min(0).max(10_000).default(0),
      })
      .refine((e) => e.ng <= e.kg, { message: 'harvest.w_ngkg', path: ['ng'] }),
  ),
});

export const saleLineSchema = z
  .object({
    crop,
    product_name: optionalText,
    kind: z.enum(['sale', 'refund']).default('sale'),
    supplier_id: z.uuid().optional().nullable(),
    buy_price: money.optional().nullable(),
    unit: z.enum(['pack', 'kg']).default('pack'),
    qty: quantity,
    price: money,
  })
  .refine((l) => l.crop !== 'X' || !!l.product_name, {
    message: 'errors.product_name',
    path: ['product_name'],
  });

export const saleDaySchema = z.object({
  date: isoDate,
  written_total: money.optional().nullable(),
  note: optionalText,
  orders: z.array(
    z.object({
      client_id: z.uuid({ message: 'errors.client_required' }),
      note: optionalText,
      lines: z.array(saleLineSchema).min(1),
    }),
  ),
});
export type SaleDayInput = z.input<typeof saleDaySchema>;

export const purchaseDaySchema = z.object({
  date: isoDate,
  note: optionalText,
  orders: z.array(
    z.object({
      supplier_id: z.uuid({ message: 'errors.supplier_required' }),
      note: optionalText,
      lines: z
        .array(
          z.object({
            crop,
            product_name: optionalText,
            unit: z.enum(['pack', 'kg']).default('pack'),
            qty: quantity,
            price: money,
          }),
        )
        .min(1),
    }),
  ),
});

const time = z
  .string()
  .regex(/^\d{2}:\d{2}$/)
  .optional()
  .nullable()
  .or(z.literal('').transform(() => null));

export const shiftPlanSchema = z.object({
  date: isoDate,
  note: optionalText,
  rows: z.array(
    z.object({
      greenhouse_id: z.uuid().optional().nullable(),
      station_id: z.uuid().optional().nullable(),
      lead_id: z.uuid().optional().nullable(),
      workers: z.coerce.number().int().min(0).max(500).optional().nullable(),
      start_time: time,
      end_time: time,
      note: optionalText,
    }),
  ),
});

export const taskSchema = z.object({
  title: trimmed.min(1, 'errors.required').max(300),
  project_id: z.uuid().optional().nullable(),
  owner_id: z.uuid().optional().nullable(),
  due_date: optionalDate,
  priority: z.enum(['urgent', 'normal', 'low']).default('normal'),
  status: z.enum(['open', 'in_progress', 'done']).default('open'),
  notes: optionalText,
});
